import React, { FC, useCallback, useEffect, useState } from "react"
import clsx from "clsx"

import { FileUpload } from "components/inputs/FileUpload"

import { enumCMSTypes } from "constants/cms"
import { ISelectOption } from "constants/interfaces"

import { CMSService } from "services/cms/cms.service"

import { DropdownMenu } from "../../inputs/DropdownMenu"
import { InputField } from "../../inputs/InputField"

export type BannerMode = "existing" | "new"

export interface BonusBannerState {
  mode: BannerMode
  newBannerName: string
  newBannerImageUrl: string
}

interface BonusBannerSectionProps {
  tenantId: string
  bannerState: BonusBannerState
  onChange: (state: BonusBannerState) => void
  selectedBannerId: string
  onSelectedBannerChange: (e: React.ChangeEvent<any>) => void
  touched?: boolean
  error?: string
}

export const BonusBannerSection: FC<BonusBannerSectionProps> = ({
  tenantId,
  bannerState,
  onChange,
  selectedBannerId,
  onSelectedBannerChange,
  touched,
  error
}) => {
  const [banners, setBanners] = useState<ISelectOption[]>([])
  const [bannerImageMap, setBannerImageMap] = useState<Record<string, string>>({})

  useEffect(() => {
    const fetchBanners = async () => {
      const res: any = await CMSService.getCMSTable(1, 100, undefined, {
        type: enumCMSTypes.BONUS_BANNER,
        active: true,
        tenant: tenantId
      })
      if (res?.data) {
        setBanners(
          res.data.map((item: any) => ({
            value: item.id,
            name: item.name
          }))
        )
        const imageMap: Record<string, string> = {}
        res.data.forEach((item: any) => {
          if (item.banner_image) imageMap[item.id] = item.banner_image
        })
        setBannerImageMap(imageMap)
      }
    }
    fetchBanners()
  }, [tenantId])

  const selectedBannerImage = bannerImageMap[selectedBannerId] ?? null

  const handleNewBannerUpload = useCallback(
    (_id: string, url: string) => {
      onChange({ ...bannerState, newBannerImageUrl: url })
    },
    [bannerState, onChange]
  )

  const setMode = (mode: BannerMode) => onChange({ ...bannerState, mode })
  const setName = (name: string) => onChange({ ...bannerState, newBannerName: name })
  const clearImage = () => onChange({ ...bannerState, newBannerImageUrl: "" })

  const modeButtonClass = (active: boolean) =>
    clsx("btn btn-sm", active ? "btn-primary bg-[rgba(169,78,206,1)] border-transparent text-white" : "btn-ghost border-gray-600")

  return (
    <div className="mt-6 rounded-lg border border-gray-600 p-4">
      <p className="text-white pb-3 text-sm font-semibold">Banner</p>

      <div className="flex gap-2 mb-4">
        <button type="button" className={modeButtonClass(bannerState.mode === "existing")} onClick={() => setMode("existing")}>
          Select Existing Banner
        </button>
        <button type="button" className={modeButtonClass(bannerState.mode === "new")} onClick={() => setMode("new")}>
          Create New Banner
        </button>
      </div>

      {bannerState.mode === "existing" && (
        <div className="grid grid-cols-3 gap-2 items-start">
          <DropdownMenu
            id="banner_image"
            title="Select Banner"
            noAllValueText
            options={banners as any}
            value={selectedBannerId}
            onChange={onSelectedBannerChange}
            invalid={Boolean(touched && error)}
            altLabelOne={touched ? error : ""}
          />
          {selectedBannerImage && (
            <div className="col-span-2 flex items-center gap-3 pt-6">
              <img
                src={selectedBannerImage}
                alt="Banner preview"
                className="h-16 w-auto max-w-xs rounded border border-gray-600 object-cover"
              />
            </div>
          )}
        </div>
      )}

      {bannerState.mode === "new" && (
        <div className="grid grid-cols-3 gap-2 items-start">
          <InputField
            id="new_banner_name"
            title="Banner Name"
            value={bannerState.newBannerName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          />
          <FileUpload
            id="new_banner_image"
            title="Banner Image"
            setFieldValue={handleNewBannerUpload}
            altLabelOne={bannerState.newBannerImageUrl ? "Uploaded ✓" : ""}
          />
          {bannerState.newBannerImageUrl && (
            <div className="flex items-center gap-2 pt-8">
              <img
                src={bannerState.newBannerImageUrl}
                alt="Banner preview"
                className="h-12 w-auto rounded border border-gray-600 object-cover"
              />
              <button type="button" className="btn btn-xs btn-ghost text-error" onClick={clearImage}>
                ✕
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
